import { BaseService } from './base';
import { ApiRequest } from '../lib/request';
import type { ApiResponse, ApiListResponse } from '../lib/response';

import { MarketingMessage, MarketingTemplateResponse, SentSMSInfo } from '../models/marketing';
import { Upload } from '../models/upload';

export class MarketingService extends BaseService<any> {
  override model = MarketingMessage;
  override modelName = 'marketing';
  override modelLabel = 'Marketing';

  async sentMessages({
    page,
    limit,
    channel,
    type,
    to,
    customer,
  }: {
    page?: number;
    limit?: number;
    channel?: string;
    type?: string;
    to?: string;
    customer?: { idCustomer: number };
  } = {}): Promise<ApiListResponse<MarketingMessage>> {
    const filter: Record<string, any> = {};

    if (channel) {
      filter.channel = channel;
    }
    if (type) {
      filter.type = type;
    }
    if (to) {
      filter.to = to;
    }
    if (customer) {
      filter.customer = customer.idCustomer;
    }

    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/sentMessages`,
      data: { filter },
      query: {
        page: (page || 1).toString(),
        ...(limit ? { limit: limit.toString() } : {}),
      },
    });

    return await this.requestList(request, MarketingMessage);
  }

  async sentMessage(
    channel: string,
    id: number,
  ): Promise<ApiResponse<MarketingMessage>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${id}/sentMessage`,
      query: { channel },
    });

    return await this.requestItem(request, MarketingMessage);
  }

  async sentSMSInfo(
    id: number,
  ): Promise<ApiResponse<SentSMSInfo>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/sentSMSInfo`,
      query: { idLocationSmsSend: id.toString() },
    });

    return await this.requestItem(request, SentSMSInfo);
  }

  async template({
    template,
    models,
  }: {
    template: string;
    models: Record<string, any>;
  }): Promise<ApiResponse<MarketingTemplateResponse>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/template`,
      data: { template, models },
    });

    return await this.requestItem(request, MarketingTemplateResponse);
  }

  async sendEmail({
    customers,
    subject,
    body,
  }: {
    customers: number[];
    subject: string;
    body: string;
  }): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/sendEmail`,
      data: { customers, subject, body },
    });

    return await this.requestItem(request, MarketingMessage);
  }

  async sendSMS({
    customers,
    body,
  }: {
    customers: number[];
    body: string;
  }): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/sendSms`,
      data: { customers, body },
    });

    return await this.requestItem(request, MarketingMessage);
  }

  async uploads({
    page = 1,
    search,
    dataType,
  }: {
    page?: number;
    search?: string | null;
    dataType?: number | null;
  } = {}): Promise<ApiListResponse<Upload>> {
    const data: Record<string, any> = {};

    if (search) {
      data.search = search;
    }
    if (dataType !== null && dataType !== undefined) {
      data.dataType = dataType;
    }

    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/uploads`,
      data,
      query: { page: page.toString() },
    });

    return await this.requestList(request, Upload);
  }
}
